/** @format */

import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Box } from '@mui/material';

function Guage({ name, data }) {
  const chartRef = useRef(null);

  const formatValue = (value) => {
    return (value / 1000).toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const percentage = (data?.value / data?.budget) * 100;

  const getActiveColor = (pct) => {
    if (pct < 95) return '#be1e2d';
    if (pct <= 100) return '#f7931d';
    return '#009344';
  };

  const activeColor = getActiveColor(percentage);

  const angle100 = 180 - (100 / 130) * 180;

  function getRow(label, value, pct) {
    let color = '';

    if (pct < -5) {
      color = 'red';
    } else if (pct >= 0) {
      color = 'green';
    } else {
      color = 'orange';
    }

    return `
      <tr>
        <td
          style="
            text-align: left;
            padding-right: 8px;
          "
        >
          ${label}
        </td>

        <td
          style="
            text-align: right;
            padding-right: 8px;
            font-family: 'Roboto Condensed', sans-serif;
            font-variant-numeric: tabular-nums;
          "
        >
          ${formatValue(value)}
        </td>

        <td
          style="
            text-align: right;
            color: ${color};
            font-family: 'Roboto Condensed', sans-serif;
            font-variant-numeric: tabular-nums;
          "
        >
          ${pct ? pct.toFixed(1) : ''}${pct ? '%' : ''}
        </td>
      </tr>
    `;
  }

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    const baseOption = {
      tooltip: {
        show: true,
        trigger: 'item',
        confine: false,
        enterable: true,

        extraCssText:
          'z-index: 99999 !important; pointer-events: auto !important; background: transparent !important; border: none !important; box-shadow: none !important; padding: 0 !important; margin: 0 !important;',

        formatter: function () {
          return `
            <div
              style="
                z-index: 10000;
                background-color: #e8e4e4;
                font-size: 0.75rem;
                padding: 8px;
                border-radius: 8px;
                opacity: 1;
                color: black;
                position: relative;
              "
            >
              <table
                style="
                  width: 100%;
                  border-collapse: collapse;
                "
              >
                ${getRow('Actual', data?.value, '')}
                ${getRow(
                  'Forecast',
                  data?.forecast,
                  data?.forecast_percentage
                )}
                ${getRow(
                  'Budget',
                  data?.budget,
                  data?.budget_percentage
                )}
                ${getRow(
                  'LY',
                  data?.last_year,
                  data?.last_yr_percentage
                )}
              </table>
            </div>
          `;
        },
      },

      title: {
        text: name,
        left: 'center',
        bottom: '0px',

        textStyle: {
          color: '#374151',
          fontSize: 15,
          fontWeight: 400,
        },
      },

      series: [
        {
          type: 'gauge',

          radius: '100%',

          center: ['50%', '60%'],

          startAngle: 180,

          endAngle: 0,

          min: 0,

          max: 130,

          splitNumber: 13,

          axisLine: {
            lineStyle: {
              width: 23,

              color: [
                [95 / 130, '#be1e2d'],
                [100 / 130, '#f7931d'],
                [1, '#009344'],
              ],
            },
          },

          pointer: {
            width: 4,

            length: '62%',

            itemStyle: {
              color: activeColor,
            },
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: false,
          },

          axisLabel: {
            distance: -38,

            fontSize: 10,

            rotate: 40,

            formatter: (value) => {
              if (Math.round(value) === 100) {
                return '100%';
              }

              return '';
            },
          },

          detail: {
            valueAnimation: true,

            fontSize: 20,

            fontWeight: 500,

            color: activeColor,

            offsetCenter: [0, '25%'],

            fontFamily: 'Roboto Condensed, sans-serif',

            formatter: () => formatValue(data?.value),
          },

          data: [
            {
              value: percentage,
            },
          ],
        },

        {
          type: 'gauge',

          radius: '100%',

          center: ['50%', '60%'],

          startAngle: angle100 + 0.4,

          endAngle: angle100 - 0.4,

          min: 0,

          max: 1,

          axisLine: {
            lineStyle: {
              width: 23,

              color: [[1, '#000000']],
            },
          },

          axisTick: {
            show: false,
          },

          splitLine: {
            show: false,
          },

          axisLabel: {
            show: false,
          },

          pointer: {
            show: false,
          },

          detail: {
            show: false,
          },
        },
      ],
    };

    chart.setOption(baseOption);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver = null;

    if (typeof ResizeObserver !== 'undefined' && chartRef.current) {
      resizeObserver = new ResizeObserver(handleResize);

      resizeObserver.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);

      resizeObserver?.disconnect();

      chart.dispose();
    };
  }, [name, data, activeColor, angle100, percentage]);

  return (
    <Box
      sx={{
        textAlign: 'center',
        flexGrow: 1,
        width: '100%',
        minWidth: 0,
      }}
    >
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: '150px',
        }}
      />
    </Box>
  );
}

export default Guage;
